/**
 * Localized structured data (schema.org JSON-LD) for the SFN landing page.
 * ---------------------------------------------------------------------------
 * Emits an Organization graph plus a FAQPage graph built from the exact FAQ
 * strings shown on the page, for every one of the 7 supported locales.
 * Keeping this in one module guarantees the rich-snippet text can never drift
 * from the rendered accordion copy.
 */
import { SFN_SOCIALS } from "@/components/SocialIcons";
import { en } from "./i18n/en";
import { de } from "./i18n/de";
import { zh } from "./i18n/zh";
import { ja } from "./i18n/ja";
import { ko } from "./i18n/ko";
import { ru } from "./i18n/ru";
import { pt } from "./i18n/pt";
import type { Dict } from "./i18n/types";

/** Canonical production origin. Every absolute URL in metadata derives from this. */
export const SITE_URL = "https://www.mealtoken24.com";

/** Build an absolute production URL from a root-relative path. */
export function absUrl(path: string): string {
  return `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
}

export const SUPPORTED_LOCALES = ["en", "de", "zh", "ja", "ko", "ru", "pt"] as const;
export type SeoLang = (typeof SUPPORTED_LOCALES)[number];

/** BCP-47 tags used for hreflang alternates and JSON-LD `inLanguage`. */
export const HREFLANG: Record<SeoLang, string> = {
  en: "en",
  de: "de",
  zh: "zh-Hans",
  ja: "ja",
  ko: "ko",
  ru: "ru",
  pt: "pt-BR",
};

const DICTS: Record<SeoLang, Dict> = { en, de, zh, ja, ko, ru, pt };

export function isSeoLang(v: unknown): v is SeoLang {
  return typeof v === "string" && (SUPPORTED_LOCALES as readonly string[]).includes(v);
}

export function dictFor(lang: SeoLang): Dict {
  return DICTS[lang] ?? en;
}

/** FAQ pairs rendered on the landing page, in display order. */
export const FAQ_KEYS = [1, 2, 3, 4] as const;

export function faqPairs(lang: SeoLang): { question: string; answer: string }[] {
  const d = dictFor(lang);
  return FAQ_KEYS.map((n) => ({
    question: String(d[`faq.q${n}` as keyof Dict] ?? en[`faq.q${n}` as keyof Dict] ?? ""),
    answer: String(d[`faq.a${n}` as keyof Dict] ?? en[`faq.a${n}` as keyof Dict] ?? ""),
  })).filter((p) => p.question && p.answer);
}

const ORG_NAME = "Sustainable Food & Eco Network";
const OG_IMAGE = absUrl("/og-image.jpg");

export function organizationJsonLd(lang: SeoLang, description: string) {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": absUrl("/#organization"),
    name: ORG_NAME,
    alternateName: ["SFN", "SFN • Sustainable Food & Eco Network"],
    description,
    url: SITE_URL,
    logo: OG_IMAGE,
    image: OG_IMAGE,
    inLanguage: HREFLANG[lang],
    sameAs: SFN_SOCIALS.map((s) => s.href),
    knowsAbout: [
      "Food security",
      "Real-world assets",
      "Solana",
      "MEAL2",
      "MEAL4",
      "Tokenized nutrition",
    ],
  };
}

export function faqPageJsonLd(lang: SeoLang) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": absUrl("/#faq"),
    inLanguage: HREFLANG[lang],
    mainEntity: faqPairs(lang).map((p) => ({
      "@type": "Question",
      name: p.question,
      acceptedAnswer: { "@type": "Answer", text: p.answer },
    })),
  };
}
